import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLocation } from '../context/LocationContext';
import { calculateCart } from '../utils/apiTransform';
import './Cart.css';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, clearCart } = useCart();
  const { selectedLocation } = useLocation();
  const [cartCalculation, setCartCalculation] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState(null);

  // Calculate cart totals whenever cart items change
  useEffect(() => {
    const fetchCartCalculation = async () => {
      if (cartItems.length === 0) {
        setCartCalculation(null);
        setCalculationError(null);
        return;
      }

      if (!selectedLocation) {
        return;
      }

      try {
        setIsCalculating(true);
        setCalculationError(null);

        const response = await calculateCart(cartItems, selectedLocation);

        if (response.status === 'error') {
          // Clear cart and show error message
          setCalculationError('Server encountered an error. Cart has been reset.');
          clearCart();
          setCartCalculation(null);
        } else {
          setCartCalculation(response);
        }
      } catch (error) {
        console.error('Error calculating cart:', error);
        setCalculationError('Failed to calculate cart totals. Please try again.');
      } finally {
        setIsCalculating(false);
      }
    };

    fetchCartCalculation();
  }, [cartItems, selectedLocation, clearCart]);

  const handleCheckout = () => {
    if (cartItems.length > 0 && cartCalculation && cartCalculation.status === 'success') {
      navigate('/checkout');
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="empty-cart">
          <img src="/logo.png" alt="Belle Journée Bakery" className="empty-cart-logo" />
          <h2>Your cart is empty</h2>
          <p>Add some delicious items to get started!</p>
          <Link to="/" className="continue-shopping-btn">
            BROWSE MENU
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        <h2 className="cart-title">Your Cart</h2>

        {calculationError && (
          <div className="error-message">
            <p>{calculationError}</p>
          </div>
        )}

        <div className="cart-items">
          {cartItems.map((item) => {
            // Find matching item from API response if available
            const apiItem = cartCalculation?.items?.find(i => i.item === item.id);

            return (
              <div key={item.id} className="cart-item">
                <div className="cart-item-image">
                  <img src={item.image} alt={item.name} />
                </div>
                <div className="cart-item-details">
                  <h3>{item.name}</h3>
                  <p className="cart-item-category">{item.category}</p>
                  <p className="cart-item-price">
                    ${apiItem ? apiItem.unit_price.toFixed(2) : item.price.toFixed(2)}
                  </p>
                </div>
                <div className="cart-item-actions">
                  <div className="quantity-controls">
                    <button
                      className="quantity-btn"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    >
                      -
                    </button>
                    <span className="quantity">{item.quantity}</span>
                    <button
                      className="quantity-btn"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                  <button
                    className="remove-btn"
                    onClick={() => removeFromCart(item.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="cart-summary">
          {isCalculating ? (
            <div className="summary-loading">
              <p>Calculating totals...</p>
            </div>
          ) : cartCalculation && cartCalculation.status === 'success' ? (
            <>
              <div className="summary-row">
                <span>Subtotal</span>
                <span>${cartCalculation.cart_summary.subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Tax</span>
                <span>${cartCalculation.cart_summary.tax.toFixed(2)}</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span>${cartCalculation.cart_summary.total.toFixed(2)}</span>
              </div>
            </>
          ) : (
            <div className="summary-row">
              <span>Calculating...</span>
            </div>
          )}

          <button
            className="checkout-btn"
            onClick={handleCheckout}
            disabled={!cartCalculation || cartCalculation.status !== 'success' || isCalculating}
          >
            Proceed to Checkout
          </button>
          <Link to="/" className="continue-shopping">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Cart;
